import { describeFirestoreError } from "../utils/firebase-error";
import {
  Injectable,
  inject,
  signal,
  computed,
  Injector,
  runInInjectionContext,
} from "@angular/core";
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  limit,
  Timestamp,
} from "@angular/fire/firestore";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { Business } from "../models/business.model";

export interface CreateBusinessResult {
  businessId: string;
  ownerUid: string;
}

/**
 * Manages the Firestore `businesses` collection.
 *
 * Creation goes through the createBusiness callable (SuperAdmin only) because it
 * must seed the first BUSINESS member in the same batch. Reads are filtered by
 * membership via Firestore rules — anyone with standing in the business, at any
 * role, can read it.
 */
@Injectable({ providedIn: "root" })
export class BusinessService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private injector = inject(Injector);
  private readonly COLLECTION = "businesses";

  loading = signal(false);
  error = signal<string | null>(null);
  businesses = signal<Business[]>([]);

  totalBusinesses = computed(() => this.businesses().length);

  /**
   * Load businesses by ID list. Use for non-SuperAdmin users so they only
   * fetch businesses they belong to (from their membership mirror), avoiding
   * collection-query rules issues.
   */
  async getBusinessesByIds(ids: string[]): Promise<Business[]> {
    if (ids.length === 0) {
      this.businesses.set([]);
      return [];
    }
    this.loading.set(true);
    this.error.set(null);
    try {
      return await runInInjectionContext(this.injector, async () => {
        const snaps = await Promise.all(
          ids.map((id) => getDoc(doc(this.firestore, this.COLLECTION, id))),
        );
        const items = snaps
          .filter((s) => s.exists())
          .map((s) => ({ id: s.id, ...s.data() } as Business));
        this.businesses.set(items);
        return items;
      });
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, "Failed to fetch businesses");
      this.error.set(message);
      console.error("Failed to fetch businesses:", err);
      return [];
    } finally {
      this.loading.set(false);
    }
  }

  async getBusinesses(): Promise<Business[]> {
    this.loading.set(true);
    this.error.set(null);

    try {
      return await runInInjectionContext(this.injector, async () => {
        const ref = collection(this.firestore, this.COLLECTION);
        const q = query(ref, orderBy("name", "asc"), limit(100));
        const snapshot = await getDocs(q);

        const items = snapshot.docs.map(
          (d) => ({ id: d.id, ...d.data() }) as Business,
        );
        this.businesses.set(items);
        return items;
      });
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, "Failed to fetch businesses");
      this.error.set(message);
      console.error("Failed to fetch businesses:", err);
      return [];
    } finally {
      this.loading.set(false);
    }
  }

  async getBusiness(id: string): Promise<Business | null> {
    try {
      return await runInInjectionContext(this.injector, async () => {
        const docRef = doc(this.firestore, this.COLLECTION, id);
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) return null;
        return { id: snapshot.id, ...snapshot.data() } as Business;
      });
    } catch (err: unknown) {
      console.error("Failed to fetch business:", err);
      return null;
    }
  }

  /**
   * Create a business and its first BUSINESS admin, via the createBusiness
   * callable. SuperAdmin only, enforced by the callable.
   *
   * Was a direct client `setDoc`, which wrote the business document and no
   * membership at all — leaving a business that no rule could see and only a
   * SuperAdmin could repair. The owner is therefore not optional: pass either an
   * existing `ownerUid`, or `ownerEmail` + `ownerDisplayName` to invite one.
   *
   * Returns the new businessId. An invited owner gets no link back — the
   * callable does not mint one, so "Forgot password" is their way in if the
   * reset email does not arrive.
   */
  async createBusiness(input: {
    name: string;
    ownerUid?: string;
    ownerEmail?: string;
    ownerDisplayName?: string;
  }): Promise<CreateBusinessResult | null> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const result = await runInInjectionContext(this.injector, async () => {
        const callable = httpsCallable<typeof input, CreateBusinessResult>(
          this.functions,
          "createBusiness",
        );
        return await callable(input);
      });

      await this.getBusinesses();
      return result.data;
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, "Failed to create business");
      this.error.set(message);
      console.error("Failed to create business:", err);
      return null;
    } finally {
      this.loading.set(false);
    }
  }

  async updateBusiness(
    id: string,
    changes: Partial<Pick<Business, "name" | "status">>,
  ): Promise<boolean> {
    this.loading.set(true);
    this.error.set(null);

    try {
      await runInInjectionContext(this.injector, async () => {
        const docRef = doc(this.firestore, this.COLLECTION, id);
        await updateDoc(docRef, { ...changes, updatedOn: Timestamp.now() });
      });
      await this.getBusinesses();
      return true;
    } catch (err: unknown) {
      const message =
        describeFirestoreError(err, "Failed to update business");
      this.error.set(message);
      console.error("Failed to update business:", err);
      return false;
    } finally {
      this.loading.set(false);
    }
  }
}
